import {
  RefObject,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import collabApi from "api/collaboration";
import { ColCollaborator, ColComment, ColComments } from "../types";
import { CollaborationVisibility } from "./constants";
import { COMMENT_TYPES } from "./hooks/useCollaborationMessage";
import { POST_STATES } from "constants/PostsView";

import { ReduxRootState } from "@/types/redux-store";
import { getCollaborators } from "@/selectors/collaborationSelectors";

import {
  getComments,
  isRequestingCommentsSelector,
} from "selectors/collaborationChatSelectors";

import { useSelector } from "react-redux";

import {
  addCollaborationComment,
  deleteCollaborationComment,
  editCollaborationComment,
  getCollaborationComments,
} from "actions/collaboration";
import { CollaborationChatProps } from "./CollaborationChat";

type AsyncActionCreator<A extends unknown[]> = (
  ...args: [...A, (result?: unknown) => void]
) => unknown;
type AsyncActionCreatorWithReject<A extends unknown[]> = (
  ...args: [...A, (result?: unknown) => void, (error?: unknown) => void]
) => unknown;

export interface CollaborationThreads {
  legacy: ColComment[];
  active: ColComment[];
}

interface UseCollaborationChatLifecycleParams {
  adminId: string;
  channelId: string;
  containerRef: RefObject<HTMLDivElement>;
  dispatch: ReturnType<typeof useDispatch>;
  hasThreads: boolean;
  userId: string;
}
export const useCollaborationChatLifecycle = ({
  adminId,
  channelId,
  containerRef,
  dispatch,
  hasThreads,
  userId,
}: UseCollaborationChatLifecycleParams) => {
  // Track if we've already scrolled to bottom for this channel
  const hasScrolledToBottomRef = useRef(false);
  const previousChannelIdRef = useRef(channelId);

  useEffect(() => {
    collabApi.subscribeToCollabChannel(userId, adminId, channelId, dispatch);

    return () => {
      collabApi.unsubscribeFromCollabChannel(adminId, channelId);
    };
  }, [adminId, channelId, dispatch, userId]);

  // Reset scroll flag when channel changes
  useEffect(() => {
    if (previousChannelIdRef.current !== channelId) {
      hasScrolledToBottomRef.current = false;
      previousChannelIdRef.current = channelId;
    }
  }, [channelId]);

  // One-time scroll to bottom when messages load
  useEffect(() => {
    if (!hasThreads || hasScrolledToBottomRef.current) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Small delay to ensure DOM is fully updated
    const timeoutId = setTimeout(() => {
      const current = containerRef.current;
      if (!current) {
        return;
      }

      current.scrollTop = current.scrollHeight;
      hasScrolledToBottomRef.current = true;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [containerRef, hasThreads]);
};

interface UseCollaborationChatResult {
  collaborators: ColCollaborator[];
  editingComment: ColComment | null;
  handleCancelEdit: () => void;
  handleMessageDeleted: (comment: ColComment) => Promise<void>;
  handleMessageSubmit: (
    text: string,
    visibility: CollaborationVisibility
  ) => Promise<boolean>;
  handleStartEdit: (comment: ColComment) => void;
  isRequestingComments: boolean;
  messagesContainerRef: RefObject<HTMLDivElement>;
  showEmptyState: boolean;
  threads: CollaborationThreads;
  isError: boolean;
  clearError: () => void;
}

export const useCollaborationChat = ({
  channelId,
  user,
  postId,
  postState,
}: CollaborationChatProps): UseCollaborationChatResult => {
  const dispatch = useDispatch();
  const [isError, setIsError] = useState(false);

  const comments = useSelector((state: ReduxRootState) =>
    getComments(state, channelId)
  ) as ColComments | undefined;
  const collaborators =
    useSelector((state: ReduxRootState) => getCollaborators(state)) ?? [];
  const isRequestingComments = useSelector((state: ReduxRootState) =>
    isRequestingCommentsSelector(state, channelId)
  );

  const [editingComment, setEditingComment] = useState<ColComment | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { threads, hasUserWrittenComments } = useMemo(() => {
    const legacy = comments?.legacyThread ?? [];
    const active = comments?.activeThread ?? [];
    const hasUserWritten = [...legacy, ...active].some(
      (comment) => comment.type === COMMENT_TYPES.USER_WRITTEN
    );

    return {
      threads: {
        legacy,
        active,
      },
      hasUserWrittenComments: hasUserWritten,
    };
  }, [comments]);

  const hasThreads = threads.legacy.length > 0 || threads.active.length > 0;
  const showEmptyState =
    !isRequestingComments &&
    !hasUserWrittenComments &&
    postState !== POST_STATES.SCHEDULED;

  const dispatchWithPromise = useCallback(
    <A extends unknown[]>(actionCreator: AsyncActionCreator<A>, ...args: A) =>
      new Promise<unknown>((resolve) => {
        dispatch(actionCreator(...args, resolve));
      }),
    [dispatch]
  );
  const dispatchWithPromiseReject = useCallback(
    <A extends unknown[]>(
      actionCreator: AsyncActionCreatorWithReject<A>,
      ...args: A
    ) =>
      new Promise<unknown>((resolve, reject) => {
        dispatch(actionCreator(...args, resolve, reject));
      }),
    [dispatch]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
  }, [setEditingComment]);

  const handleStartEdit = useCallback(
    (comment: ColComment) => {
      setEditingComment((previous) =>
        previous?._id === comment._id ? null : comment
      );
    },
    [setEditingComment]
  );

  const handleMessageSubmit = useCallback(
    async (text: string, visibility: CollaborationVisibility) => {
      try {
        if (editingComment) {
          await dispatchWithPromiseReject(
            editCollaborationComment,
            channelId,
            editingComment._id,
            text,
            visibility
          );
          setEditingComment(null);
        } else {
          const metadata = {
            post: { postId },
            postType: postState.toLowerCase(),
          };
          await dispatchWithPromiseReject(
            addCollaborationComment,
            channelId,
            text,
            metadata,
            visibility
          );
        }

        dispatch(getCollaborationComments(channelId));
        return true;
      } catch (error) {
        console.log("Error submitting collaboration comment:", error);
        setIsError(true);

        return false;
      }
    },
    [
      channelId,
      dispatch,
      dispatchWithPromiseReject,
      editingComment,
      postId,
      postState,
      setEditingComment,
    ]
  );

  const handleMessageDeleted = useCallback(
    async (comment: ColComment) => {
      await dispatchWithPromise(
        deleteCollaborationComment,
        channelId,
        comment._id
      );
      setEditingComment((previous) =>
        previous?._id === comment._id ? null : previous
      );
      dispatch(getCollaborationComments(channelId));
    },
    [channelId, dispatch, dispatchWithPromise, setEditingComment]
  );

  useCollaborationChatLifecycle({
    adminId: user.adminId,
    channelId,
    containerRef: messagesContainerRef,
    dispatch,
    hasThreads,
    userId: user.userId,
  });

  return {
    collaborators,
    editingComment,
    handleCancelEdit,
    handleMessageDeleted,
    handleMessageSubmit,
    handleStartEdit,
    isRequestingComments,
    messagesContainerRef,
    showEmptyState,
    threads,
    isError,
    clearError: () => {
      setIsError(false);
    },
  };
};
